import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { ApiError, documentsApi, jobsApi, llmApi } from '@/lib/api'
import { noteEmailUnverified } from '@/layouts/verificationGate'
import type { EvaluationDocument, RecognitionArm } from '@/types/domain'

import { cockpitKeys, type CockpitState } from '../useCockpit'
import { filterQuery, type DocumentFilter } from './documentsModel'

/**
 * The Dokumente tab's own server state: the paged listing, the no-match
 * listing behind the Problemfälle panel, and every mutation the screen can
 * start.
 *
 * The list is deliberately **not** in `useCockpit`: it is paged, filtered and
 * selected per screen, and caching every combination in the shared hook would
 * make the cockpit's contract enormous and stale. What does stay central is
 * the cadence — `cockpit.intervals.documents` — so the polling policy is
 * still written down once.
 */

export const PAGE_SIZE = 25

/** How many no-match documents the Problemfälle panel loads. Each one is a
 *  tall card with four actions; past a handful the panel stops being a list
 *  of problems and becomes the screen. The rest are reachable through the
 *  "Kein Treffer" filter, which the panel links to. */
export const PROBLEM_LIMIT = 5

export interface DocumentsView {
  filter: DocumentFilter
  page: number
}

export function useDocumentList(cockpit: CockpitState, view: DocumentsView) {
  const { templateId } = cockpit
  return useQuery({
    queryKey: cockpitKeys.documents(templateId, view),
    queryFn: () =>
      documentsApi.list(templateId, {
        ...filterQuery(view.filter),
        page: view.page,
        pageSize: PAGE_SIZE,
      }),
    enabled: templateId.length > 0,
    // Paging and switching filters must not blank the table: the old rows
    // stay put, dimmed, until the new ones land.
    placeholderData: keepPreviousData,
    staleTime: 5_000,
    refetchInterval: cockpit.intervals.documents,
  })
}

/** The no-match documents, in their own query so the Problemfälle panel is
 *  correct whichever filter the table is on. Same key prefix, so
 *  `cockpit.invalidate.documents()` refreshes it too. */
export function useNoMatchDocuments(cockpit: CockpitState, enabled: boolean) {
  const { templateId } = cockpit
  return useQuery({
    queryKey: cockpitKeys.documents(templateId, { problems: PROBLEM_LIMIT }),
    queryFn: () =>
      documentsApi.list(templateId, { status: 'noMatch', page: 1, pageSize: PROBLEM_LIMIT }),
    enabled: enabled && templateId.length > 0,
    staleTime: 10_000,
    refetchInterval: cockpit.intervals.documents,
  })
}

// -- Selection -------------------------------------------------------------

export function useSelection() {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())

  const toggle = useCallback((id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  /** The header box selects and clears **this page**, which is what it can
   *  honestly do: the other pages are not loaded, and a "select all 400"
   *  that quietly meant 25 would delete the wrong things. */
  const toggleAll = useCallback((ids: string[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  return { selected, toggle, toggleAll, clear }
}

// -- Mutations -------------------------------------------------------------

export interface MoveInput {
  documentId: string
  targetTemplateId: string
  analyze: boolean
}

export function useDocumentActions(cockpit: CockpitState) {
  const queryClient = useQueryClient()
  const { templateId, invalidate } = cockpit

  /** After anything that starts or removes work: the rows, the counts and
   *  the job list all move together. Deleting also frees pages against the
   *  plan, which the shell's usage line reads from its own key. */
  const refreshAll = useCallback(
    async (touchedStorage = false) => {
      await Promise.all([
        invalidate.documents(),
        invalidate.counts(),
        invalidate.jobs(),
        touchedStorage
          ? queryClient.invalidateQueries({ queryKey: ['usage'] })
          : Promise.resolve(),
      ])
    },
    [invalidate, queryClient],
  )

  const analyse = useMutation({
    mutationFn: (input: { documentIds: string[]; forcePage?: number }) =>
      documentsApi.startAnalysis(templateId, input.documentIds, {
        ...(input.forcePage != null ? { forcePage: input.forcePage } : {}),
      }),
    onError: noteEmailUnverified,
    onSuccess: () => refreshAll(),
  })

  /**
   * One recognition arm over a selection.
   *
   * `apply` is required by `llmApi.startArmRun` and always passed from the
   * dialog, never defaulted here: the worker's own default is `true`, which
   * rewrites the delivered values — and the next run then reads its own
   * output as the baseline and agrees with itself.
   */
  const runArm = useMutation({
    mutationFn: (input: { documentIds: string[]; arm: RecognitionArm; apply: boolean }) =>
      llmApi.startArmRun(templateId, input),
    onError: noteEmailUnverified,
    onSuccess: () => refreshAll(),
  })

  const remove = useMutation({
    mutationFn: async (documentIds: string[]) => {
      if (documentIds.length === 1) {
        await documentsApi.remove(templateId, documentIds[0])
        return 1
      }
      return documentsApi.batchDelete(templateId, documentIds)
    },
    onSuccess: () => refreshAll(true),
  })

  const move = useMutation({
    mutationFn: (input: MoveInput) =>
      documentsApi.move(templateId, input.documentId, input.targetTemplateId, {
        analyze: input.analyze,
      }),
    onError: noteEmailUnverified,
    onSuccess: () => refreshAll(true),
  })

  /**
   * Stops every job that is still running.
   *
   * Sequentially, and a job that has finished in the meantime (400
   * `job_not_cancellable`) is not an error worth showing: the person asked
   * for it to stop, and it has.
   */
  const cancelJobs = useMutation({
    mutationFn: async (jobIds: string[]) => {
      let cancelled = 0
      for (const jobId of jobIds) {
        try {
          await jobsApi.cancel(templateId, jobId)
          cancelled += 1
        } catch (error) {
          if (ApiError.from(error).code !== 'job_not_cancellable') throw error
        }
      }
      return cancelled
    },
    onSuccess: () => refreshAll(),
  })

  return { analyse, runArm, remove, move, cancelJobs }
}

// -- Error copy ------------------------------------------------------------

/**
 * A refusal in German.
 *
 * The gateway's own texts are English and written for a developer ("Too many
 * concurrent uploads", "job is not cancellable"), so the handful this screen
 * can actually provoke get a sentence of their own and everything else falls
 * back to the message the gateway sent rather than to a shrug.
 */
export function documentActionError(error: unknown): string {
  const api = ApiError.from(error)
  switch (api.code) {
    case 'email_unverified':
      return 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Bis dahin sind neue Analysen pausiert.'
    case 'no_credits':
      return 'Das Guthaben reicht für diesen Lauf nicht aus.'
    case 'page_limit_reached':
    case 'template_limit_reached':
      return 'Das Limit Ihres Tarifs ist erreicht.'
    case 'network_error':
      return 'Keine Verbindung zum Server.'
    case 'unknown_arm':
      return api.message
    // The three ways a KI run is refused before it starts. The gateway
    // checks these at enqueue rather than letting the worker refuse, so the
    // answer arrives here instead of as a failed job in the Aufträge list —
    // where it read `arm llm_image produced nothing` and named nothing the
    // person could do about it.
    case 'llm_consent_missing':
      return 'Dieses Musterformular ist nicht für die KI-Prüfung freigegeben. Die Freigabe erteilen Sie unter Einstellungen → KI-Zweitmeinung.'
    case 'llm_consent_mismatch':
      return 'Die gespeicherte Freigabe nennt einen anderen KI-Anbieter oder eine andere Region als dieser Server verwendet. Bitte erteilen Sie sie unter Einstellungen → KI-Zweitmeinung neu.'
    case 'llm_disabled':
      return 'Auf diesem Server ist keine KI-Prüfung eingerichtet.'
    default:
      break
  }
  if (api.isForbidden) return 'Dafür fehlt Ihnen die Berechtigung.'
  if (api.isNotFound) return 'Das Dokument gibt es nicht mehr.'
  return api.message || 'Die Aktion ist fehlgeschlagen.'
}

/** True when the refusal is the e-mail pause, which a move can work around
 *  by asking for no analysis (the gateway's documented retry). */
export function isVerificationPause(error: unknown): boolean {
  return ApiError.from(error).code === 'email_unverified'
}

export function documentsOf(page?: { docs: EvaluationDocument[] }): EvaluationDocument[] {
  return page?.docs ?? []
}
